import { useEffect, useState } from 'react';
import { ManageWorkerAnalytics } from './ManageWorkerAnalytics';
import TableChartIcon from '@mui/icons-material/TableChart';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import AnalyticsDashboard from './Dashboard';
import { Typography, Box, TextField, Button, Paper, Stack } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import dayjs from 'dayjs';
import { fetchAllMonthlyAnalytics, fetchAllWorkerAnalytics } from '../../services/workeranalytics.service';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import { fetchAllProjectAnalytics, getAllActiveProjects } from '../../services/project.service';
import { getTotalNumberOfWorkers } from '../../services/workdone.service';
import { getOffset } from '../../utils/utilityFunctions';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { calculateWorkerFinancials } from '../../utils/workerAnalyticsMath';

const useStyles = makeStyles({
	root: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
	},
	container: {
		flex: 1,
		width: '100%',
		display: 'flex',
	},
	header: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	iconContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	separator: {
		fontSize: 30,
	},
	icon: {
		fontSize: '30px',
	},
	activeIcon: {
		color: 'blue',
	},
	inactiveIcon: {
		color: 'black',
	},
});

const WorkerAnalytics = () => {
	const styles = useStyles();
	const history = useHistory();
	const location = useLocation();

	const [isDashboard, setIsDashboard] = useState<boolean>(false);
	const [toDate, setToDate] = useState<string>(moment().format('YYYY-MM-DD'));
	const [fromDate, setFromDate] = useState<string>(moment().startOf('month').format('YYYY-MM-DD'));
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [workerAnalyticsTotalLength, setWorkerAnalyticsTotalLength] = useState<number>(0);
	const [workerAnalyticsData, setWorkerAnalyticsData] = useState<any>([]);
	const [projectAnalyticsData, setProjectAnalyticsData] = useState<any>([]);
	const [monthlyAnalyticsData, setMonthlyAnalyticsData] = useState<any>([]);
	const [pageSize, setPageSize] = useState<number>(10);
	const [revenue, setRevenue] = useState<number>(0);
	const [salary, setSalary] = useState<number>(0);
	const [otherCosts, setOtherCosts] = useState<number>(0);
	const [expenses, setExpenses] = useState<number>(0);
	const [totalProfitLoss, setTotalProfitLoss] = useState<number>(0);
	const [monthsOfSalaryPaid, setMonthsOfSalaryPaid] = useState<number>(0);
	const [noOfActiveProjects, setNoOfActiveProjects] = useState<number>(0);
	const [totalNoOfWorkers, setTotalNoOfWorkers] = useState<number>(0);
	const [pastNoOfMonths, setPastNoOfMonths] = useState<number>(3);

	const handleDashboardClick = () => {
		setIsDashboard(true);
		history.push('?view=dashboard');
	};

	const handleWorkerAnalyticsClick = () => {
		setIsDashboard(false);
		history.push('?view=detailed');
	};

	const getMonthDifference = (startDate, endDate) => {
		const start = new Date(startDate);
		const end = new Date(endDate);

		// Check if the dates are within the same month and year
		if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
			return 1;
		}

		// Calculate the month difference
		const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

		return months + 1; // Add 1 to include the start month
	};

	useEffect(() => {
		const query = new URLSearchParams(location.search);
		const view = query.get('view');
		if (view === 'dashboard') {
			setIsDashboard(true);
		} else if (view === 'detailed') {
			setIsDashboard(false);
		} else {
			// If no view is specified, default to the dashboard and update the URL
			setIsDashboard(true);
			history.replace('?view=dashboard');
		}
	}, [location.search]);

	const getDayDifference = (startDate: string | number | Date, endDate: string | number | Date): number | null => {
		const start = new Date(startDate);
		const end = new Date(endDate);

		if (isNaN(start.getTime()) || isNaN(end.getTime())) {
			return null;
		}

		const differenceInMilliseconds = end.getTime() - start.getTime();

		// Convert milliseconds to days
		const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

		return Math.ceil(differenceInDays);
	};

	const loadDataToGrid = async (pageNumber?: number, size?: number) => {
		try {
			setLoading(true);
			let tmpList = [];
			let projectTmpList = [];
			let monthlyAnalyticsTmpList = [];
			let totalRevenue = 0;
			let totalSalary = 0;
			let totalOtherCosts = 0;
			let totalExpenses = 0;
			let totalProfitLoss = 0;
			let noOfActiveProjects = 0;
			let totalNoOfWorkers = 0;

			// Detailed view path (paginated): treat 0 as a valid page
			if (typeof pageNumber === 'number' && typeof size === 'number') {
				// Fetch full dataset for the date range (no pagination), then sort and slice for the requested page
				const fullResult = await fetchAllWorkerAnalytics(
					moment(fromDate).format('YYYY-MM-DD'),
					moment(toDate).utcOffset(0, true).endOf('day').format('YYYY-MM-DD HH:mm:ss')
				);

				const allRows = fullResult?.data?.data?.rows || [];
				let fullList = [];

				for (let i = 0; i < allRows.length; i++) {
					const info = allRows[i];
					const financials = calculateWorkerFinancials(info);
					const row = {
						id: info.id,
						workerName: info.name,
						workDoneEntriesCount: Number(info.totalWorkDoneEntries) || 0,
						hipWorkDoneEntries: Number(info.hipWorkDoneEntries) || 0,
						nonHipWorkDoneEntries: Number(info.nonHipWorkDoneEntries) || 0,
						creditedQuantityByUnit: info.creditedQuantityByUnit,
						hipCreditedQuantityByUnit: info.hipCreditedQuantityByUnit,
						nonHipCreditedQuantityByUnit: info.nonHipCreditedQuantityByUnit,
						hipWorkBreakdown: info.hipWorkBreakdown || '',
						nonHipWorkBreakdown: info.nonHipWorkBreakdown || '',
						jointWorkDoneEntries: Number(info.jointWorkDoneEntries) || 0,
						collaboratorNames: info.collaboratorNames || '',
						hipJointWorkDoneEntries: Number(info.hipJointWorkDoneEntries) || 0,
						nonHipJointWorkDoneEntries: Number(info.nonHipJointWorkDoneEntries) || 0,
						hipCollaboratorNames: info.hipCollaboratorNames || '',
						nonHipCollaboratorNames: info.nonHipCollaboratorNames || '',
						...financials,
					};
					fullList.push(row);
				}

				// Global sort first, then paginate
				fullList.sort((a, b) =>
					(a.workerName || '').localeCompare(b.workerName || '', undefined, { sensitivity: 'base' })
				);

				const totalItems = fullList.length;
				setWorkerAnalyticsTotalLength(totalItems);

				const startIndex = pageNumber * size;
				const endIndex = startIndex + size;
				tmpList = fullList.slice(startIndex, endIndex);
			} else {
				const result = await fetchAllWorkerAnalytics(
					moment(fromDate).format('YYYY-MM-DD'),
					moment(toDate).utcOffset(0, true).endOf('day').format('YYYY-MM-DD HH:mm:ss')
				);
				const projectAnalyticsResult = await fetchAllProjectAnalytics(
					moment(fromDate).format('YYYY-MM-DD'),
					moment(toDate).format('YYYY-MM-DD')
				);
				const monthlyAnalyticsResult = await fetchAllMonthlyAnalytics(pastNoOfMonths);
				const allActiveProjects = await getAllActiveProjects();
				noOfActiveProjects = allActiveProjects.data.data[0].activeProjects;

				const totalNumberOfWorkers = await getTotalNumberOfWorkers();
				totalNoOfWorkers = totalNumberOfWorkers.data.data[0].allWorkers;

				for (let i = 0; i < result?.data?.data?.rows?.length; i++) {
					const info = result.data?.data?.rows[i];
					const financials = calculateWorkerFinancials(info);
					const row = {
						id: info.id,
						workerName: info.name,
						workDoneEntriesCount: Number(info.totalWorkDoneEntries) || 0,
						hipWorkDoneEntries: Number(info.hipWorkDoneEntries) || 0,
						nonHipWorkDoneEntries: Number(info.nonHipWorkDoneEntries) || 0,
						creditedQuantityByUnit: info.creditedQuantityByUnit,
						hipCreditedQuantityByUnit: info.hipCreditedQuantityByUnit,
						nonHipCreditedQuantityByUnit: info.nonHipCreditedQuantityByUnit,
						hipWorkBreakdown: info.hipWorkBreakdown || '',
						nonHipWorkBreakdown: info.nonHipWorkBreakdown || '',
						jointWorkDoneEntries: Number(info.jointWorkDoneEntries) || 0,
						collaboratorNames: info.collaboratorNames || '',
						hipJointWorkDoneEntries: Number(info.hipJointWorkDoneEntries) || 0,
						nonHipJointWorkDoneEntries: Number(info.nonHipJointWorkDoneEntries) || 0,
						hipCollaboratorNames: info.hipCollaboratorNames || '',
						nonHipCollaboratorNames: info.nonHipCollaboratorNames || '',
						...financials,
					};
					tmpList.push(row);
					totalRevenue += row.revenue;
					totalSalary += row.salary;
					totalOtherCosts += row.expenses;
					totalExpenses += row.totalExpenses;
					totalProfitLoss += row.profitLoss;
				}
				for (let i = 0; i < projectAnalyticsResult?.data?.data?.rows?.length; i++) {
					const info = projectAnalyticsResult.data?.data?.rows[i];
					const row = {
						projectName: info.name,
						totalSellingPrice: info.totalSellingPrice,
					};
					projectTmpList.push(row);
				}
				for (let i = 0; i < monthlyAnalyticsResult?.data?.data?.rows?.length; i++) {
					const info = monthlyAnalyticsResult.data?.data?.rows[i];
					const [year, month] = info.month.split('-');

					const row = {
						year: year,
						month: month,
						totalRevenue: Number(info.total_revenue) || 0,
						totalExpenses: Number(info.total_expenses) || 0,
						totalProfits: Number(info.total_profit) || 0,
					};
					monthlyAnalyticsTmpList.push(row);
				}

				const monthsOfSalaryPaid = getMonthDifference(fromDate, toDate);
				setMonthsOfSalaryPaid(monthsOfSalaryPaid);
				setProjectAnalyticsData([...projectTmpList]);
				setWorkerAnalyticsData([...tmpList]);
				setMonthlyAnalyticsData([...monthlyAnalyticsTmpList]);
				setRevenue(totalRevenue);
				setSalary(totalSalary);
				setOtherCosts(totalOtherCosts);
				setExpenses(totalExpenses);
				setTotalProfitLoss(totalProfitLoss);
				setNoOfActiveProjects(noOfActiveProjects);
				setTotalNoOfWorkers(totalNoOfWorkers);
			}

			// Set state after processing all rows
			setWorkerAnalyticsData([...tmpList]);
			setLoading(false);
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message);
			setLoading(false);
		}
	};

	const handleUpdateData = () => {
		setPage(0);
		loadDataToGrid(0, pageSize);
	};

	return (
		<div className={styles.container}>
			<div className={styles.root}>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					alignItems={{ xs: 'flex-start', md: 'center' }}
					justifyContent='space-between'
					spacing={2}
					sx={{ mb: 2 }}>
					<Box>
						<Typography variant='h4' sx={{ fontWeight: 750, letterSpacing: '-0.025em' }}>
							Worker Analytics
						</Typography>
						<Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
							See how much work each worker completed and where credit was shared.
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', p: 0.5, gap: 0.5, bgcolor: '#f1f5f9', borderRadius: 2.5 }}>
						<Button
							size='small'
							startIcon={<DashboardRoundedIcon />}
							variant={isDashboard ? 'contained' : 'text'}
							onClick={handleDashboardClick}
							sx={{ px: 1.5, borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}>
							Overview
						</Button>
						<Button
							size='small'
							startIcon={<TableChartIcon />}
							variant={!isDashboard ? 'contained' : 'text'}
							onClick={handleWorkerAnalyticsClick}
							sx={{ px: 1.5, borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}>
							Worker breakdown
						</Button>
					</Box>
				</Stack>

				<Paper variant='outlined' sx={{ mb: 2.5, p: 1.5, borderRadius: 3, borderColor: '#dfe6ee', bgcolor: '#fbfcfe' }}>
					<Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.25}>
						<Stack direction='row' alignItems='center' spacing={1} sx={{ mr: { md: 'auto' } }}>
							<Box
								sx={{
									width: 34,
									height: 34,
									borderRadius: 2,
									bgcolor: '#e8f0fb',
									color: '#245b9b',
									display: 'grid',
									placeItems: 'center',
								}}>
								<DateRangeOutlinedIcon fontSize='small' />
							</Box>
							<Box>
								<Typography variant='body2' sx={{ fontWeight: 750 }}>
									Work period
								</Typography>
								<Typography variant='caption' color='text.secondary'>
									Based on the date the work was performed
								</Typography>
							</Box>
						</Stack>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='From'
								value={fromDate}
								onChange={(newValue) => setFromDate(dayjs(newValue).format('YYYY-MM-DD'))}
								inputFormat='DD-MM-YYYY'
								renderInput={(params) => (
									<TextField
										{...params}
										size='small'
										sx={{ width: { xs: '100%', md: 180 } }}
										inputProps={{ ...params.inputProps, readOnly: true }}
									/>
								)}
								maxDate={dayjs(toDate).format('YYYY-MM-DD')}
							/>
							<DatePicker
								label='To'
								value={toDate}
								onChange={(newValue: any) => setToDate(dayjs(newValue).format('YYYY-MM-DD'))}
								inputFormat='DD-MM-YYYY'
								renderInput={(params) => (
									<TextField
										{...params}
										size='small'
										sx={{ width: { xs: '100%', md: 180 } }}
										inputProps={{ ...params.inputProps, readOnly: true }}
									/>
								)}
								maxDate={dayjs().format('YYYY-MM-DD')}
								minDate={dayjs(fromDate).format('YYYY-MM-DD')}
							/>
						</LocalizationProvider>
						<Button
							variant='contained'
							onClick={handleUpdateData}
							sx={{ minHeight: 40, px: 2, borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}>
							Apply
						</Button>
					</Stack>
				</Paper>
				{isDashboard ? (
					<AnalyticsDashboard
						loadDataToGrid={() => loadDataToGrid()}
						fromDate={fromDate}
						toDate={toDate}
						loading={loading}
						setLoading={setLoading}
						workerAnalyticsData={workerAnalyticsData}
						workerAnalyticsTotalLength={workerAnalyticsTotalLength}
						revenue={revenue}
						salary={salary}
						otherCosts={otherCosts}
						expenses={expenses}
						totalProfitLoss={totalProfitLoss}
						monthsOfSalaryPaid={monthsOfSalaryPaid}
						projectAnalyticsData={projectAnalyticsData}
						noOfActiveProjects={noOfActiveProjects}
						totalNoOfWorkers={totalNoOfWorkers}
						monthlyAnalyticsData={monthlyAnalyticsData}
					/>
				) : (
					<ManageWorkerAnalytics
						loadDataToGrid={(page: number, pageSize: number) => loadDataToGrid(page, pageSize)}
						page={page}
						setPage={setPage}
						pageSize={pageSize}
						setPageSize={setPageSize}
						fromDate={fromDate}
						toDate={toDate}
						loading={loading}
						setLoading={setLoading}
						workerAnalyticsData={workerAnalyticsData}
						workerAnalyticsTotalLength={workerAnalyticsTotalLength}
					/>
				)}
			</div>
		</div>
	);
};

export default WorkerAnalytics;
